import { Injectable } from '@angular/core';
import { distinctUntilChanged, map, Observable, shareReplay, startWith, combineLatest, tap, timer, switchMap } from 'rxjs';
import { actionId, actionType } from 'src/mqtt/actions-list';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { getActionKey } from 'src/mqtt/utils/device-name-parser';
import { LightDevice } from '../interfaces/lights.interface';
import { OnOff } from 'src/graphql/generated/schema';
import { DeviceRegistryService } from 'src/app/services/device-registry.service';

@Injectable({
  providedIn: 'root'
})
export class LightsControlService {

  private _lightsUnits$!: Observable<string[]>;

  constructor(
    private mqtt: MqttConnectorService,
    private deviceRegistry: DeviceRegistryService,
  ) {
    // Combine device registry (for immediate display) with MQTT updates
    this._lightsUnits$ = combineLatest([
      this.deviceRegistry.getRegistry(),
      this.mqtt.getMessageListener(`${actionType.light}.${actionId.allLights}`).pipe(
        startWith(undefined) // Allow registry to show first
      )
    ]).pipe(
      map(([registry, message]) => {
        // First, check if we have discovered devices from MQTT
        const attributes = message?.reported?.attributes;
        if (attributes && attributes['entity_id']) {
          const discoveredDevices = attributes['entity_id'].map((entityId: string) => getActionKey(entityId, actionId.lights));
          // Register these devices for future use
          this.deviceRegistry.registerDevices('lights', discoveredDevices);
          return discoveredDevices;
        }
        
        // If no MQTT data yet, use expected devices from registry
        return this.deviceRegistry.getExpectedDevices('lights');
      }),
      // Only emit when the array actually changes
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public get lightsUnits() {
    return this._lightsUnits$;
  }

  public getDeviceBaseData(deviceId: string, deviceName: string, deviceState: IShadowState): LightDevice | null {
    const attributes = deviceState?.reported?.attributes;
    if (!attributes) return null;

    if (!deviceName?.length && typeof attributes['friendly_name'] === 'string') {
      deviceName = attributes['friendly_name'].substring(0, attributes['friendly_name'].indexOf(' '))
    }

    const device: LightDevice = {
      deviceId,
      title: deviceName,
      active: <OnOff>deviceState?.reported?.state?.toUpperCase() === OnOff.On,
      isUnavailable: deviceState?.reported?.state === 'unavailable'
    }

    // Extract brightness from attributes if available
    if (attributes['brightness'] !== undefined) {
      device.brightness = Math.round((attributes['brightness'] / 255) * 100);
    }

    // Extract color temperature if available
    if (attributes['color_temp'] !== undefined) {
      device.temperature = attributes['color_temp'];
    }

    // Extract RGB color if available
    if (attributes['rgb_color'] && Array.isArray(attributes['rgb_color'])) {
      const [r, g, b] = attributes['rgb_color'];
      device.brightColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    return device;
  }

  public switchLight(deviceId: string, enable: boolean): Observable<void> {
    // Turn-on goes through the vOS script so it restores the saved brightness
    // and the correct colour mode. A bare turn_on would inherit whatever the
    // device last remembered — which the night light overwrites every evening.
    if (enable) {
      return this.mqtt.triggerScript('lights_turn_on', {
        light_group: `${actionType.light}.${deviceId}${actionId.lights}`,
      });
    }
    return this.mqtt.setStateValue(
      `${actionType.light}.${deviceId}${actionId.lights}`,
      'off'
    )
  }

  public setLightColor(deviceId: string, color: string): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputText}.${deviceId}${actionId.lightColor}`,
      color
    )
  }

  public setBrightness(deviceId: string, brightness: number): Observable<void> {
    // Write to the input_number the UI reads back (settings slider, grid card,
    // admin dashboard). Writing the light entity's brightness attribute instead
    // left the value unpersisted and desynced from the dashboard (VI-207).
    return this.mqtt.setStateValue(
      `${actionType.inputNumber}.${deviceId}${actionId.lightBrightness}`,
      brightness
    )
  }

  public setAutoOffTime(deviceId: string, offTime: number): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputNumber}.${deviceId}${actionId.lightsAutoOffTime}`,
      offTime
    )
  }

  public setCircadianLights(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.circadianLight}`,
      enable ? 'on' : 'off'
    )
  }

  public setNightLight(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.nightLight}`,
      enable ? 'on' : 'off'
    )
  }

  public setNightLightBrightness(brightness: number): Observable<void> {
    // Night Light only dims the Spa Suite (hallway_a); a single helper backs it.
    return this.mqtt.setStateValue(
      `${actionType.inputNumber}.hallway_a${actionId.nightLightBrightness}`,
      brightness
    )
  }

  public setAutoOff(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.lightsAutoOff}`,
      enable ? 'on' : 'off'
    )
  }

  public setAutoOn(deviceId: string, enable: boolean): Observable<void> {
    return this.mqtt.setStateValue(
      `${actionType.inputBoolean}.${deviceId}${actionId.lightsAutoOn}`,
      enable ? 'on' : 'off'
    )
  }
}
